import React from 'react';
import { priceStrategies } from 'appConstants/dishes';
import { useTranslation } from 'react-i18next';
import Text from 'components/Text/Text';
import { Badge } from 'components/Badge';
import FullInfoField from 'components/FullInfoField/FullInfoField';
import style from './Portion.module.scss';
import DataController from 'components/DataController/DataController';
import classNames from 'classnames';
import { IGetModifierRestaurantPortion } from 'types/modifiers';

interface IProps {
	portion: IGetModifierRestaurantPortion;
	onClickInfo: () => void;
	onClickEdit: () => void;
	onClickDelete: () => void;
}

export const Portion: React.FC<IProps> = ({ portion, onClickInfo, onClickEdit, onClickDelete }) => {
	const { t } = useTranslation('form');
	const actionRowClasses = classNames(style.action, style.row);
	const priceStrategy = priceStrategies.find((strategy) => strategy.id === portion.priceStrategy);
	return (
		<div className={style.container}>
			<div className={style.header}>
				<div className={style.labelRow}>
					<Text
						className={style.labelText}
						translationKey={portion.name || '-'}
						size="s"
						withTooltip={true}
					/>
					<div />
				</div>
				<div className={style.controlButtonsWrapper}>
					{portion.isCreatedFromPos ? (
						<Badge color="blue">
							<Text
								translationKey="POS"
								asFragment={true}
								data-test={`isCreatedFromPos=${portion.isCreatedFromPos}`}
							/>
						</Badge>
					) : (
						<Badge color="darkPurple">
							<Text
								translationKey="AMS"
								asFragment={true}
								data-test={`isCreatedFromPos=${portion.isCreatedFromPos}`}
							/>
						</Badge>
					)}
					<div className={actionRowClasses}>
						<DataController onClickInfo={onClickInfo} size="smallBtn" />
					</div>
				</div>
			</div>
			<div className={style.data}>
				<div className={style.dataItem}>
					<Text
						className={style.noShrink}
						translationNameSpace="form"
						translationKey="posNameLabel"
						theme="neutralLight"
						size="xs"
					/>
					<Text translationKey={portion.posName ?? '-'} size="xs" withTooltip={true} />
				</div>
				<div className={classNames(style.dataItem, style.noShrink)}>
					<Text
						className={style.noShrink}
						translationNameSpace="form"
						translationKey="signedPriceLabel"
						theme="neutralLight"
						size="xs"
					/>
					<Text
						className={style.noShrink}
						translationKey={
							typeof portion.price === 'number'
								? `${portion.currencySymbol || ''} ${portion.price || 0}`
								: '-'
						}
						size="xs"
					/>
				</div>
				<div className={classNames(style.dataItem, style.noShrink)}>
					<Text
						className={style.noShrink}
						translationNameSpace="form"
						translationKey="signedSupplementPriceLabel"
						theme="neutralLight"
						size="xs"
					/>
					<Text
						className={style.noShrink}
						translationKey={
							typeof portion.supplementPrice === 'number'
								? `${portion.currencySymbol || ''} ${portion.supplementPrice || 0}`
								: '-'
						}
						size="xs"
					/>
				</div>
				<div className={classNames(style.dataItem, style.noShrink)}>
					<Text
						className={style.noShrink}
						translationNameSpace="form"
						translationKey="priceStrategyLabel"
						theme="neutralLight"
						size="xs"
					/>
					<Text
						className={style.noShrink}
						translationKey={priceStrategy ? t(priceStrategy.name) : '-'}
						size="xs"
					/>
				</div>
				<div className={style.dataItem}>
					<Text
						className={style.noShrink}
						translationNameSpace="form"
						translationKey="articleNumberLabel"
						theme="neutralLight"
						size="xs"
					/>
					<Text translationKey={portion.articleNumber ?? '-'} size="xs" withTooltip={true} />
				</div>
				<div className={style.dataItem}>
					<Text
						className={style.noShrink}
						translationNameSpace="form"
						translationKey="externalIdLabel"
						theme="neutralLight"
						size="xs"
					/>
					<Text translationKey={portion.externalId ?? '-'} size="xs" withTooltip={true} />
				</div>
				<FullInfoField
					containerClassName={style.fullInfoFieldContainer}
					inputWrapperClassName={style.fullInfoFieldWrapper}
					className={style.fullInfoFieldInput}
					translationNameSpace="form"
					value="isDisabledLabel"
					prefixIcon="asCheckbox"
					prefixIconName={portion.isDisabled ? 'checkMark' : 'crossMark'}
					prefixIconIsChecked={portion.isDisabled}
					useInForm={true}
				/>
			</div>
		</div>
	);
};
